import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import MenuShell from "@/components/menu-shell";
import PopupCloseButton from "@/components/popup-close-button";
import styles from "@/components/rename-shortcut-folder-popup.module.css";
import useCustomInput from "@/hooks/use-custom-input";
import * as signals from "@/signals";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { FormEvent, JSX, useId, useState } from "react";

function RenameShortcutFolderPopup(): JSX.Element {
	const queryClient = useQueryClient();

	const [isClosing, setIsClosing] = useState<boolean>(false);

	const nameInputId = useId();

	const nameInput = useCustomInput<HTMLInputElement>({
		setValue: (newValue) => {
			signals.editingFolder.value = {
				...signals.editingFolder.value,
				folderName: newValue,
			};
		},
	});

	const handleClose = (): void => {
		setIsClosing(true);
		window.setTimeout(() => {
			signals.renameShortcutFolderPopup.value = {
				...signals.renameShortcutFolderPopup.value,
				isShown: false,
			};
			setIsClosing(false);
		}, 350);
	};

	const handleSubmit = async (
		event: FormEvent<HTMLFormElement>,
	): Promise<void> => {
		event.preventDefault();
		handleClose();
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).renameBookmarkFolder(
				signals.editingFolder.value.id,
				signals.editingFolder.value.folderName,
			);
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	return (
		<MenuShell
			className={styles["rename-shortcut-folder-popup"]}
			isClosingMenu={isClosing}
			isModal={true}
			isTransformOriginDeclared={true}
			onClose={handleClose}
			shouldUseAlternativeOverflowPosition={true}
			show={signals.renameShortcutFolderPopup.value.isShown}
			x={signals.renameShortcutFolderPopup.value.position.left}
			y={signals.renameShortcutFolderPopup.value.position.top}
		>
			<form onSubmit={(event) => void handleSubmit(event)}>
				<div className={styles["title-bar"]}>
					<label htmlFor={nameInputId}>{t("renameFolder")}</label>
					<PopupCloseButton onClick={handleClose} />
				</div>
				<input
					autoComplete="off"
					className="input"
					data-autofocus
					id={nameInputId}
					placeholder={t("folderName")}
					value={signals.editingFolder.value.folderName}
					{...nameInput}
				/>
				<button
					className={styles["submit-button"]}
					type="submit"
				>
					{t("save")}
				</button>
			</form>
		</MenuShell>
	);
}

export default RenameShortcutFolderPopup;
